import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { IconButton } from '~/components/isolated/IconButton';
import { SimpleCounter } from '~/components/isolated/SimpleCounter';
import { useProductCard } from '~/components/ProductCard/hooks/useProductCard';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { userStore } from '~/stores';
import type { Product } from '~/stores/CategoriesStore';
import {
  convertPenceToPounds,
  formatPriceWithCurrency,
} from '~/utils/formaters';
import { getApplicableMultibuyLevel } from '~/utils/multibuy';

interface SearchResultItemDropdownProps {
  product: Product;
  onClose: (e?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
}

export const SearchResultItemDropdown: React.FC<SearchResultItemDropdownProps> =
  observer(({ product, onClose }) => {
    const { handleClickProduct } = useCategoryAnalytics();

    const {
      currentItem,
      offerSku,
      image,
      currentPrice,
      cartItemsCount,
      isFavorite,
      productHasManyVariants,
      isBundle,
      isMultiBuy,
      limit,
      toggleFavorite,
      changeCount,
      openSelectSKUModal,
    } = useProductCard(product);

    const handleProductClick = useCallback(() => {
      handleClickProduct(product);
      onClose();
    }, [product, handleClickProduct, onClose]);

    const addButtonHandler = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (productHasManyVariants && !isBundle) {
        onClose();
        openSelectSKUModal();
      } else {
        changeCount(cartItemsCount + 1, 'add');
      }
    };

    const handleToggleFavorite = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toggleFavorite();
    };

    const handleIncrement = () => {
      changeCount(cartItemsCount + 1, 'add');
    };

    const handleDecrement = () => {
      changeCount(cartItemsCount - 1, 'remove');
    };

    const handleCounterClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Keep the raw (unformatted) amount inside `useMemo`; currency formatting
    // is done at the top level because `formatPriceWithCurrency` reads global
    // context via a hook and must never be called from inside `useMemo`.
    const displayPriceRaw = useMemo(() => {
      if (!isMultiBuy) {
        return currentPrice;
      }

      const applicableLevel = getApplicableMultibuyLevel(
        currentItem.multibuyPriceLevels,
        cartItemsCount,
      );

      return applicableLevel
        ? convertPenceToPounds(applicableLevel.unitPrice)
        : currentPrice;
    }, [
      isMultiBuy,
      currentItem.multibuyPriceLevels,
      cartItemsCount,
      currentPrice,
    ]);

    const displayPrice = formatPriceWithCurrency(displayPriceRaw);

    return (
      <Link
        to={`/p/${product.slug}`}
        state={{ offerSku }}
        className="group flex h-[88px] cursor-default gap-3 rounded-[16px] bg-transparent p-2 no-underline transition-colors duration-200 hover:bg-navigation-surface-2"
        onClick={handleProductClick}
      >
        <div className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-[12px] bg-navigation-surface p-1 transition-colors duration-200 group-hover:bg-navigation-surface-2">
          <CloudflareResponseImage
            src={currentItem.images[0] ?? image ?? ''}
            srcParams={{ width: 72, height: 72, fit: 'contain' }}
            alt={currentItem.name}
            className="h-full w-full object-contain"
            loading="lazy"
          />
        </div>

        <div className="flex min-w-0 flex-1 items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h4 className="m-0 overflow-hidden text-ellipsis whitespace-nowrap text-navigation-text-primary [font:var(--text-300-medium)]">
              {currentItem.name}
            </h4>
            <span className="text-navigation-text-tertiary [font:var(--text-200-medium)]">
              {displayPrice}
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {cartItemsCount > 0 ? (
              <div onClick={handleCounterClick}>
                <SimpleCounter
                  count={cartItemsCount}
                  onIncrement={handleIncrement}
                  onDecrement={handleDecrement}
                  variant="secondary"
                  colorVariant="navigation"
                  className="h-12 w-[155px]!"
                  limit={limit}
                  hideTooltip
                />
              </div>
            ) : (
              <>
                {(!company.hideFavoritesForGuests ||
                  userStore.isAuthorized) && (
                  <IconButton
                    className={cn([
                      'rounded-[8px]! border border-navigation-stroke-primary! text-navigation-icon-primary! hover:bg-navigation-surface-2!',
                      isFavorite && 'text-icon-brand',
                    ])}
                    icon={isFavorite ? 'heart-fill' : 'heart'}
                    iconSize={20}
                    size="L"
                    variant="outline"
                    onClick={handleToggleFavorite}
                    aria-label="Toggle favorite"
                  />
                )}
                <IconButton
                  className="rounded-[8px]! border border-navigation-stroke-primary! text-navigation-icon-primary! hover:bg-navigation-surface-2!"
                  icon={company.isDubaiExpressCompany ? 'cart-3.1' : 'plus-3.1'}
                  iconSize={company.isDubaiExpressCompany ? 20 : 16}
                  size="L"
                  variant="outline"
                  onClick={addButtonHandler}
                  aria-label="Add to cart"
                />
              </>
            )}
          </div>
        </div>
      </Link>
    );
  });
